import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const baseDictionarySource = {
  en: {
    translation: {
      navigation: {
        dashboards: 'Dashboards',
        strategy: 'Strategy',
        financials: 'Financials',
        process: 'Process',
        agentic_process: 'Agentic Process',
        agent_miner: 'Agent Miner',
        process_analysis: 'Process Analysis',
        adherence: 'Adherence',
        integrations: 'Integrations',
        work_periods: 'Work Periods',
      },
      common: {
        // Common terms used across multiple modules
        active: 'Active',
        inactive: 'Inactive',
        current: 'Current',
        calibrated: 'Calibrated',
        work_unit: 'Task',
        work_units: '$t(common.work_unit)s',
        parent_task: 'Epic',
        estimation: 'Points',
        estimation_singular: 'Point',
        work_unit_description: 'Description',
        description: 'Description',
        priority: 'Priority',
        assignee: 'Assignee',
        reporter: 'Reporter',
        component: 'Component',
        components_plural: 'Components',
        label: 'Label',
        labels_plural: 'Labels',
        key: 'Key',
        time_original_estimate: 'Time Original Estimate',
        work_unit_details: 'Work Unit Details',
        name: 'Name',
        type: 'Type',
        created: 'Created',
        created_date: 'Created Date',
        completed: 'Completed',
        status_history: 'Status History',
        initiative: 'Initiative',
        initiatives: '$t(common.initiative)s',
        outcome: 'Objective',
        outcomes: '$t(common.outcome)s',
        strategic_goal: 'Strategic Goal',
        strategic_goals: 'Strategic Goals',
        bug: 'Bug',
        debt: 'Debt',
        epic: '$t(common.parent_task)',
        epics: '$t(common.parent_task)s',
        close_filters: 'Close filters',
        filters: 'Filters',
        sprint: 'Sprint',
        sprints: '$t(common.sprint)s',
        last_updated: 'Last Updated',
        portfolio: 'Portfolio',
        portfolios: '$t(common.portfolio)s',
        project: 'Project',
        projects: '$t(common.project)s',
        team: 'Team',
        teams: 'Teams',
        board: 'Board',
        boards: '$t(common.board)s',
        agents: 'Agents',
        unit: 'Unit',
        units: 'Units',
        user: 'User',
        users: 'Users',
        all_users: 'All Users',
        pull_request: 'Pull Request',
        pull_requests: 'Pull Requests',
        repository: 'Repository',
        repositories: 'Repositories',
        work_period: 'Work Period',
        work_periods: 'Work Periods',
        target: 'Target',
        targets: 'Targets',
        subtask: 'Subtask',
        subtasks: '$t(common.subtask)s',
        progress: 'Progress',
        assigned_boards: 'Assigned $t(common.boards)',
        no_boards_selected: 'No $t(common.board, lowercase)s selected',
        related_tasks: 'Related $t(common.work_units)',
        settings: 'Settings',
        notification: 'Notification',
        visit_source_page: 'Visit source page',
        use_groups: 'Use Groups',
        add_bookmark: 'Bookmark',
        remove_bookmark: 'Remove Bookmark',
        bookmark_error: 'Failed to update bookmark',
        hide: 'Hide',
        hide_error: 'Failed to hide $t(common.outcome, lowercase)',
        unhide: 'Unhide',
        unhide_error: 'Failed to unhide $t(common.outcome, lowercase)',
        hidden: 'Hidden',
        include_done_tasks: 'Include Done $t(common.work_units)',
        include_done_initiatives: 'Include Done $t(common.initiatives)',
        include_done_epics: 'Include Done $t(common.epics)',
        workflow_steps: {
          backlog: 'Backlog',
          in_progress: 'In Progress',
          in_review: 'In Review',
          in_test: 'In Test',
          deployable: 'Deployable',
          done: 'Done',
          waiting: 'Waiting',
        },
        integrations: {
          teams_select: 'Below Teams, select the team you would like to integrate.',
        },
        upload: {
          xlsx: 'Upload XLSX File',
        },
        actions: {
          add: 'Add',
          add_new: 'Add New',
          back: 'Back',
          edit: 'Edit',
          delete: 'Delete',
          download: 'Download',
          save: 'Save',
          search: 'Search',
          cancel: 'Cancel',
          create: 'Create',
          reset: 'Reset',
          export: 'Export',
          close: 'Close',
          compare: 'Compare',
          remove: 'Remove',
          apply: 'Apply',
        },
        status: {
          status: 'Status',
          statuses: 'Statuses',
          done: 'Done',
          in_progress: 'In Progress',
          blocked: 'Blocked',
        },
        ai: {
          assistant: 'AI Assistant',
        },
        dashboard: {
          dashboard: 'Dashboard',
          dashboards: 'Dashboards',
          template: 'Template',
          templates: 'Templates',
          data_entry: 'Data Entry',
          widget: 'Widget',
          widgets: 'Widgets',
          loading: 'Loading...',
          error_loading: 'Error loading data',
          create_widget_help: 'Easily create a new widget to tailor your dashboard to your needs.',
          create_title: 'Create New Dashboard',
          delete_initiative: 'Delete $t(common.initiative)',
          delete_initiative_message:
            'This will delete the {name} $t(common.initiative, lowercase) from Bloomfilter. It does not impact any integrated source systems.',
          continue: 'Continue',
          create_button: 'Create Dashboard',
        },
        measures: 'Measures',
        trends: 'Trends',
        charts: {
          line_chart: 'Line Chart',
          pie_chart: 'Pie Chart',
          bar_chart: 'Bar Chart',
        },
        form: {
          email: 'Email',
          name: 'Name',
          password: 'Password',
          confirm_password: 'Confirm Password',
          create_account_form: 'Create Account Form',
          login_form: 'Login Form',
          forgot_password_form: 'Forgot Password Form',
          forgot_password: 'Forgot Your Password?',
          reset_password: 'Reset Password',
          remember_password: 'Remember your password?',
          login_to_bloomfilter: 'Log in to Bloomfilter',
          reset_help: "Don't worry! We'll help you reset it quickly.",
          enter_email: 'Enter the email associated with your Bloomfilter account.',
          create_organization: 'Create Organization',
          organization_name: 'Organization Name',
          organization_description:
            'Organizations are spaces where you can access and view data from your integrations.',
        },
        placeholders: {
          email: 'Enter your email address',
          name: 'Enter your full name',
          password: 'Enter your password',
          confirm_password: 'Confirm your password',
          organization: 'Enter your organization',
          select_category: 'Select category',
          select_team: 'Select Team',
          select_measure: 'Select measure',
          select_workflow: 'Select a workflow',
          select_date: 'Select date',
          pick_dates: 'Pick dates range',
          pick_year: 'Pick year',
          select_a_group: 'Select a group',
        },
        currency: {
          code: 'USD',
          dollar_value: '${{value}}',
        },
        empty_filter: {
          no_objects_found: 'No {{objectName}} found.',
          try_different_filter: 'Please try a different filter.',
        },
        table_pagination_summary: 'Showing {{start}} to {{end}} of {{total}} items',
        overview: {
          title: 'KPIs Overview',
        },
        tooltips: {
          max_selection_limit: 'You can select up to {{count}} items at a time',
          max_comparison_limit: 'You can compare up to {{count}} items at a time',
        },
        labels: {
          not_available: 'N/A',
          no_data: 'No data available',
          no_filtered_data: 'No data available for the selected filters',
          no_filters_applied: 'No filters selected. Choose filters to see results',
          reset_filters: 'Reset Filters',
          select_all: 'Select All',
          deselect_all: 'Deselect All',
          no_data_for_item: 'No data available for the selected item',
        },
      },
      process_analysis: {
        title: 'Process Analysis',
        main_section: {
          vs: 'vs',
          select_for_comparison: 'Select for Comparison',
          one_portfolio: '1 $t(common.portfolio)',
          multiple_portfolios: '{{count}} $t(common.portfolios)',
          no_portfolios_available: 'No $t(common.portfolio, lowercase)s available',
          one_project: '1 $t(common.project)',
          multiple_projects: '{{count}} $t(common.projects)',
          no_projects_available: 'No $t(common.project, lowercase)s available',
          select_project: 'Select $t(common.project)',
          no_teams_available: 'No teams available',
          one_board: '1 $t(common.board)',
          multiple_boards: '{{count}} $t(common.boards)',
          boards_count: '{{count}} $t(common.boards)',
          no_boards_available: 'No $t(common.board, lowercase)s available',
          no_boards_found: 'No $t(common.board, lowercase)s found',
          select_board: 'Select $t(common.board)',
          search_by_project_or_board_name:
            'Search by $t(common.project, lowercase) or $t(common.board, lowercase) name',
          search_by_team_name: 'Search by team name',
          no_teams_found: 'No teams found',
          reset_comparison: 'Reset Comparison',
          no_entities_available: 'No entities available',
          units_completed: '{{units}} Completed',
        },
        view_mode: {
          measure_comparison: 'Measure Comparison',
          capacity: 'Capacity',
          initiative_overview: '$t(common.initiative) Overview',
          initiative_completion: '$t(common.initiative) Completion',
          initiative_focus: '$t(common.initiative) Focus',
          monthly_averages: 'Monthly Averages',
          key_measures_over_time: 'Key Measures Over Time',
        },
        kpi: {
          overall_portfolio_score: 'Overall $t(common.portfolio) Score',
          average_reaction_time: 'Average Reaction Time',
          average_cycle_time: 'Average Cycle Time',
          measurement_name: 'Measurement Name',
          portfolio_velocity: '$t(common.portfolio) Velocity',
          from_target: 'From Target',
          from_last_month: 'from last month',
          from_last_week: 'from last week',
          story_points_month: 'story points/month',
          strategic_allocation: 'Strategic Allocation',
          quality_allocation: 'Quality Allocation',
          contributors: 'Contributors',
          total_throughput: 'Total Throughput',
          unit_percentage: '%',
          unit_users: 'users',
          unit_tasks: '$t(common.work_unit, lowercase)s',
          unit_days: 'days',
        },
        filters: {
          portfolio_comparison: '$t(common.portfolio) Comparison',
          team_comparison: 'Team Comparison',
          board_comparison: '$t(common.board) Comparison',
          trends: 'Trends',
          no_initiatives_available: 'No $t(common.initiatives) available',
          key_measures: 'Key Measures',
          points: '$t(common.estimation)',
        },
        points_filter: {
          all: 'All $t(common.estimation)',
          none: 'No $t(common.estimation)',
        },
        units: {
          tasks: '$t(common.work_units)',
          points: '$t(common.estimation)',
          days: 'Days',
          percentage: '%',
          tasks_per_hour: '$t(common.work_units)/h',
        },
        date_range: {
          format: '{{startDate}} to {{endDate}}',
        },
        measures: {
          lead_time: 'Lead Time',
          reaction_time: 'Reaction Time',
          cycle_time: 'Cycle Time',
          review_time: 'Review Time',
          throughput: 'Throughput',
          velocity: 'Velocity',
          points_acceptance_rate: '$t(common.estimation) Acceptance Rate',
          tasks_acceptance_rate: '$t(common.work_units) Acceptance Rate',
          bulk_status_changes: 'Bulk Status Changes',
          tasks_moving_backwards: '$t(common.work_units) Moving Backwards',
          stale_tasks: 'Stale $t(common.work_units)',
          missing_days: 'Missing Days',
          readiness: 'Readiness',
          quality: 'Quality',
          declined_change_requests: 'Declined Change Requests',
          untested_tasks: 'Untested $t(common.work_units)',
          cycle_time_inconsistency: 'Cycle Time Inconsistency',
          work_plan_inconsistency: 'Work Plan Inconsistency',
          complexity: 'Complexity',
          strategy: 'Strategy',
          independence_technical_debt: 'Independence Technical Debt',
          tech_debt: 'Technical Debt $t(common.work_units)',
          defect_ratio_measure: 'Defect Ratio Measure',
          multiple_point_changes_per_task: 'Multiple $t(common.estimation) Changes per $t(common.work_unit)',
          independence: 'Independence',
        },
        workload_distribution: {
          capacity_work_units: 'Capacity ($t(common.work_units))',
          capacity_estimation: 'Capacity ($t(common.estimation))',
          workload_distribution: 'Workload Distribution',
          tooltips: {
            capacity: 'Total number of completed $t(common.work_units) over the time period',
            estimation: 'Total $t(common.estimation) completed over the time period',
            distribution: 'Distribution of work across teams',
          },
        },
        assessment: {
          pace: {
            title: 'Work pace explorer',
          },
          burnt_title: 'Burnt',
          burnt_help: '% of work period elapsed',
          done_help: 'work finished',
          work_unit_process_map: 'Work Unit Process Map',
          considered_work_units: 'Considered $t(common.work_units)',
          scope_selected: 'for the scope selected',
          this_sprint: 'This {{sprint}}',
          goals: 'Goals',
          goals_for_this_sprint: '{{goals}} for this {{sprint}}',
          key_measures: {
            strategy: 'Strategy',
            complexity: 'Complexity',
            quality: 'Quality',
            scope_creep: 'Scope',
            readiness: 'Readiness',
            independence: 'Independence',
            initial_committed_tasks: 'Initial Committed Tasks',
            initial_committed_points: 'Initial Committed Points',
            review_time: 'Review Time',
            declined_change_requests: 'PR closed (%)',
          },
        },
        initiative_completion: {
          title: '$t(common.initiative) Completion',
          initiatives: '$t(common.initiatives)',
          line: 'Line',
          trend: 'Trend',
        },
        initiative_focus: {
          title: '$t(common.initiative) Focus',
          initiatives: '$t(common.initiatives)',
          line: 'Line',
          trend: 'Trend',
        },
        key_measures: {
          readiness_label:
            'The number of work units which have a name, description, and $t(common.estimation). Note: $t(common.estimation) intentionally set to 0 are considered estimated.',
          complexity_label: 'The average $t(common.estimation) of the work units in the scope selected.',
        },
        scope: {
          title: 'Scope',
          legend: 'Legend',
        },
        side_section: {
          actual_value: 'Actual Value',
          average: 'Average',
          trend_slope: 'Trend Slope',
          forecast: 'Forecast',
          targets: 'Targets',
          no_targets_defined: 'No targets defined for this measure',
          create_targets_prompt: 'Set targets for this measure quickly and easily right here.',
          create_target: 'Create Target',
          show_target_on_graph: 'Show this target on graph',
          target_value_label: 'Target Value:',
          target_frequency_label: 'Target Frequency:',
          date_label: 'Date:',
          ongoing: 'Ongoing',
          details: 'Details',
          work_units: 'Work Units',
        },
        work_units: {
          created: 'Created',
          created_date: 'Created Date',
          type: 'Type',
          points: '$t(common.estimation)',
          sort_by: 'Sort by:',
          no_work_units_found: 'No $t(common.work_units) found for this data point',
          total_count: 'Total: {{count}}',
          work_units_count: '{{count}} $t(common.work_units)',
          work_units_count_singular: '{{count}} $t(common.work_unit)',
        },
      },
      strategy: {
        tabs: {
          initiatives: '$t(common.initiatives)',
          goals: '$t(common.strategic_goals)',
          outcomes: '$t(common.outcomes)',
          mind_map: 'Mind Map',
          roadmap: 'Roadmap',
          overview: 'Overview',
        },
        mind_map: {
          unassigned_initiatives: 'Unassigned $t(common.initiatives)',
          strategic_goals_and_objectives: '$t(common.strategic_goals) & $t(common.outcomes)',
          budget: 'Budget',
          cost: 'Cost',
          not_set: 'Not set',
        },
        milestones: {
          milestone: 'milestone',
          past_due: 'Past Due',
          add_to_get_started: 'Add a $t(strategy.milestones.milestone) to get started',
          select_to_view_details: 'Select a $t(strategy.milestones.milestone) to view its details',
          created_success: 'Milestone created successfully',
          created_error: 'Failed to create milestone',
          updated_success: 'Milestone updated successfully',
          updated_error: 'Failed to update milestone',
          deleted_success: 'Milestone deleted successfully',
          deleted_error: 'Failed to delete milestone',
          validation: {
            at_least_one_epic: 'Please select at least one $t(common.epic).',
          },
          form: {
            description: 'Define the milestone details and timeline',
            name_label: 'Milestone Name',
            name_placeholder: 'Enter milestone name',
            description_label: 'Description',
            description_placeholder: 'Enter milestone description (optional)',
            target_end_date_label: 'Target End Date',
            date_placeholder: 'Select date',
            delete_button: 'Delete Milestone',
            cancel: 'Cancel',
            next_step: 'Next',
            back: 'Back',
            save_changes: 'Save Changes',
            complete_and_create: 'Complete and Create',
            delete_confirmation: 'Delete Milestone?',
            delete_confirmation_message: 'Are you sure you want to delete this milestone?',
            epic_description: 'Select the $t(common.parent_task, lowercase)s that this milestone will track.',
            search_and_select_epics: 'Search and select $t(common.parent_task, lowercase)s',
          },
        },
        roadmap: {
          no_goal: 'No $t(common.strategic_goals)',
          no_outcome: 'No $t(common.outcomes)',
        },
        initiatives: {
          initiative: '$t(common.initiative)',
          initiatives: '$t(common.initiatives)',
          epics: '$t(common.epics)',
          throughput: 'Throughput',
          work_progress: 'Work Progress',
          strategic_progress: 'Strategic Progress',
          create: 'Create $t(common.initiative)',
          edit: 'Edit $t(common.initiative)',
          no_initiatives: 'No $t(common.initiatives) have been created yet',
          no_epics: 'No $t(common.epics)',
          no_date: 'No date',
          no_end_date: 'No End Date',
          step_indicator: '(Step {{step}}/2)',
          unsaved_changes: 'You may have unsaved changes. Are you sure you want to close without saving?',
          created_success: '{{name}} has been created successfully.',
          updated_success: '{{name}} has been successfully updated.',
          deleted_success: '{{name}} has been successfully removed.',
          create_error: 'Failed to create $t(common.initiative): {{error}}',
          unknown_error: 'Unknown error occurred',
          table_view: 'Table',
          tree_map_view: 'Tree Map',
          group_by: 'Group by:',
          metric: 'Metric:',
          search_placeholder: 'Search by $t(common.initiative) name or ID',
          export: 'Export',
          export_csv: 'Export as CSV',
          create_presentation: 'Create Presentation',
          back: 'Back',
          initiatives_title: '$t(common.initiatives)',
          edit_button: 'Edit $t(common.initiative)',
          no_description: 'No description available.',
          more_initiative_details: 'More $t(common.initiative) Details',
          other: 'Other',
          combined_initiatives: 'Combined Low-Value $t(common.initiatives)',
          statuses: {
            at_risk: 'At Risk',
            off_track: 'Off Track',
            on_track: 'On Track',
            created: 'Created',
            not_active: 'Not Active',
            not_started: 'Not Started',
            unknown: 'Unknown',
            empty: 'Empty',
          },
          validation: {
            start_date_before_end_date: 'Start date must be before end date',
            end_date_future: 'End date must be in the future',
            end_date_after_start_date: 'End date must be after start date',
          },
          presentation: {
            title: 'Create Google Slides Presentation',
            auth_required:
              'You need to authorize Google access first. Please click the button below to connect your Google account.',
            name_label: 'Presentation Name',
            name_placeholder: 'Enter presentation name',
            will_create:
              'This will create a Google Slides presentation with {{count}} $t(common.initiative, lowercase)',
            will_create_plural:
              'This will create a Google Slides presentation with {{count}} $t(common.initiative, lowercase)s',
            will_create_prefix: 'This will create a Google Slides presentation with',
            count_warning: 'Best to limit it to 5 or fewer $t(common.initiatives)',
            connect_google: 'Connect Google Account',
            created_success: 'Presentation "{{name}}" created successfully. Click OK to open it.',
            creation_error: 'Error: {{error}}',
          },
          form: {
            description:
              '{{entity}} represent broad strategic goals and $t(common.outcome, lowercase)s for your organization.',
            name_label: 'Name',
            name_placeholder: 'Enter {{entity}} name here',
            description_label: 'Description',
            description_placeholder: 'Enter {{entity}} description here',
            start_date_label: 'Start Date',
            end_date_label: 'End Date',
            date_placeholder: 'Pick date',
            business_value_label: 'Relative Business Value',
            business_value_placeholder: 'Enter relative business value here',
            numeric_label: 'Numeric',
            dollar_label: 'Dollar',
            delete_button: 'Delete $t(common.initiative)',
            next_step: 'Next Step',
            back: 'Back',
            cancel: 'Cancel',
            save_changes: 'Save Changes',
            complete_and_create: 'Complete and Create',
            epic_description:
              '$t(common.epics) contribute to achieving $t(common.initiatives). $t(common.epics) are groups of work consisting of many $t(common.work_unit, lowercase)s.',
            select_epics: 'Select $t(common.epics)',
            search_and_select_epics: 'Search and select $t(common.epic, lowercase)s',
          },
          table: {
            id: 'ID',
            strategic_goal_id: '$t(common.strategic_goal) $t(strategy.initiatives.table.id)',
            strategic_goal_name: '$t(common.strategic_goal) $t(strategy.initiatives.table.name)',
            outcome_id: '$t(common.outcome) $t(strategy.initiatives.table.id)',
            outcome_name: '$t(common.outcome) $t(strategy.initiatives.table.name)',
            initiative_id: '$t(common.initiative) $t(strategy.initiatives.table.id)',
            initiative_name: '$t(common.initiative) $t(strategy.initiatives.table.name)',
            epic_id: '$t(common.epic) $t(strategy.initiatives.table.id)',
            epic_name: '$t(common.epic) $t(strategy.initiatives.table.name)',
            name: 'Name',
            completion: 'Completion',
            business_value: 'Business Value',
            target_end: 'Target End',
            cost_to_date: 'Cost to Date',
            parent_outcomes: '$t(common.outcomes)',
            teams: 'Teams',
            empty: 'Empty',
            tasks: '$t(common.work_units)',
          },
          metrics: {
            tasks: '$t(common.work_units)',
            points: '$t(common.estimation)',
            units: 'Units',
            cost_by_initiative: 'Cost by $t(common.initiative)',
            effort_by_initiative: 'Effort by $t(common.initiative)',
          },
          labels: {
            same_as_start_date: 'Same as Start Date',
            same_as_end_date: 'Same as End Date',
            status_by_points: 'Status (By $t(common.estimation))',
            status_by_tasks: 'Status (By $t(common.work_units))',
            no_data: '-',
            hide_empty_epics: 'Hide empty $t(common.epic, lowercase)s',
          },
          delete: {
            title: 'Delete $t(common.initiative)',
            description_first: 'This will delete the <strong>{{name}}</strong>.',
            description_second:
              'This action will delete the $t(common.initiative) from Bloomfilter. It aligns with the major project areas, but will not affect any integrated source systems.',
            confirm: 'Confirm and Delete',
          },
        },
        goals: {
          presentation: {
            title: 'Create Google Slides Presentation',
            auth_required:
              'You need to authorize Google access first. Please click the button below to connect your Google account.',
            name_label: 'Presentation Name',
            name_placeholder: 'Enter presentation name',
            will_create_prefix: 'This will create a Google Slides presentation with',
            count_warning: 'Best to limit it to 5 or fewer $t(common.strategic_goals)',
            connect_google: 'Connect Google Account',
            created_success: 'Presentation "{{name}}" created successfully. Click OK to open it.',
            creation_error: 'Error: {{error}}',
          },
        },
        outcomes: {
          empty: 'No $t(common.outcomes, lowercase) found',
          search_placeholder: 'Search $t(common.outcomes, lowercase)...',
          by_status: '$t(common.outcomes) by Status',
          by_state: '$t(common.outcomes) by State',
          presentation: {
            title: 'Create Google Slides Presentation',
            auth_required:
              'You need to authorize Google access first. Please click the button below to connect your Google account.',
            name_label: 'Presentation Name',
            name_placeholder: 'Enter presentation name',
            will_create_prefix: 'This will create a Google Slides presentation with',
            count_warning: 'Best to limit it to 5 or fewer $t(common.outcomes)',
            connect_google: 'Connect Google Account',
            created_success: 'Presentation "{{name}}" created successfully. Click OK to open it.',
            creation_error: 'Error: {{error}}',
          },
        },
        overview: {
          all_time: 'All Time',
          no_initiatives: 'No $t(common.initiatives)',
          no_outcomes: 'No $t(common.outcomes)',
          no_associated_goals: 'No associated $t(common.strategic_goals)',
          title: 'KPIs Overview',
          by_status: '$t(common.initiatives) by Statuses',
          by_state: '$t(common.initiatives) by State',
          completion: 'Overall $t(common.initiatives) Completion',
          portfolio_throughput: '$t(common.portfolio) Throughput',
          tasks_completed: '$t(common.work_units) Completed',
          last_30_days: 'Last 30 Days',
          info_cards: {
            overall_progress: 'Overall Progress',
            active_initiatives: 'Active $t(common.initiatives)',
            last_30_days_throughput: 'Last 30 Days Throughput',
            last_30_days_cycle_time: 'Last 30 Days Cycle Time',
            average_risk_score: 'Average Risk Score',
            team_count: 'Team Count',
            epic_count: '$t(common.epic) Count',
            initiative_health: '$t(common.initiative) Health',
            days: 'days',
            on_track: 'On Track',
            at_risk: 'At Risk',
            off_track: 'Off Track',
            unknown: 'Unknown',
          },
          tooltips: {
            overall_progress:
              'The percentage of work completed across all $t(common.initiatives) in this {{entity}}. Calculated by dividing completed $t(common.work_units) by total $t(common.work_units).',
            active_initiatives:
              'The number of $t(common.initiatives) currently in progress that contribute to this {{entity}}.',
            throughput:
              'The average number of $t(common.work_units) completed per day over the last 30 days across all contributing $t(common.initiatives).',
            last_30_days_throughput:
              'The average number of $t(common.work_units) completed per day over the last 30 days across all contributing $t(common.initiatives).',
            cycle_time:
              "The average time in days it takes to complete a $t(common.work_unit), measured from when work starts to when it's marked done.",
            last_30_days_cycle_time:
              "The average time in days it takes to complete a $t(common.work_unit), measured from when work starts to when it's marked done.",
            risk_score:
              'The average AI-calculated risk score across all $t(common.initiatives), ranging from 0 (low risk) to 10 (high risk). Based on progress, blockers, and delivery patterns.',
            average_risk_score:
              'The average AI-calculated risk score across all $t(common.initiatives), ranging from 0 (low risk) to 10 (high risk). Based on progress, blockers, and delivery patterns.',
            team_count:
              'The number of unique teams or $t(common.project, lowercase)s contributing work to this {{entity}}.',
            epic_count:
              'The total number of $t(common.parent_task, lowercase)s associated with $t(common.initiatives) in this {{entity}}.',
            initiative_health:
              'A breakdown of $t(common.initiative) health status based on progress relative to time elapsed. On Track: progressing as expected, At Risk: slightly behind, Off Track: significantly behind.',
            progress_over_time:
              'Shows the completion percentage for each $t(common.initiative) over time. This helps you see which $t(common.initiatives) are progressing and which may be stalled.',
            team_focus_over_time:
              "Shows the percentage of each team's total work that was dedicated to this {{entity}} each month. Higher percentages indicate more focus on this {{entity}}.",
            work_committed_by_project:
              'Shows when $t(common.work_units) were added or committed to each $t(common.project, lowercase). This helps you understand work intake patterns and identify when teams started working on this {{entity}}.',
            work_completed_by_project:
              "Shows when $t(common.work_units) were finished by each $t(common.project, lowercase). Compare this with work committed to understand each team's delivery patterns and flow efficiency.",
            work_committed_completed:
              'Compares $t(common.work_units) committed (added/started) vs. completed (finished) by each $t(common.project, lowercase) over time. Each month shows two bars side by side - committed (C) and completed (D) - to help you track intake vs. throughput and identify flow bottlenecks.',
          },
          sections: {
            thirty_day_summary: '30-Day Executive Summary',
            outcome_risk: '{{entity}} Risk Analysis',
            risk_summary: 'Risk Summary',
            recommended_actions: 'Recommended Actions',
            progress_over_time: 'Progress Over Time',
            team_focus_over_time: 'Team Focus Over Time',
            work_committed_by_project: 'Work Committed by $t(common.project)',
            work_completed_by_project: 'Work Completed by $t(common.project)',
            work_committed_completed: 'Work Committed & Completed by $t(common.project)',
            initiatives_table: '$t(common.initiatives)',
          },
          table: {
            initiative_name: '$t(common.initiative)',
            progress: 'Progress',
            total_tasks: 'Total $t(common.work_units)',
            total_complete: 'Completed $t(common.work_units)',
            completed_this_month: 'Completed This Month',
            risk_score: 'Risk Score',
          },
          charts: {
            completion_percentage: 'Completion %',
            focus_percentage: 'Focus %',
            month: 'Month',
            tasks_committed: '$t(common.work_units) Committed',
            tasks_completed: '$t(common.work_units) Completed',
            work_units: '$t(common.work_units)',
          },
          empty_states: {
            no_data: 'No data available',
            no_work_committed_data: 'No work committed data available',
            no_work_completed_data: 'No work completed data available',
            no_work_data: 'No work data available',
            no_thirty_day_summary:
              'No 30-day summary available yet. Check back after the AI annotation $t(common.work_unit, lowercase) has run.',
            no_risk_analysis: 'No risk analysis available yet. Enable AI features to see risk insights.',
          },
        },
        filters: {
          status: 'Status',
          all_statuses: 'All Statuses',
          state: 'State',
          activity: 'Activity',
          active_inactive: 'Active / Inactive',
          source: 'Source',
          all_sources: 'All Sources',
          parent_outcomes: '$t(common.outcomes)',
          all_outcomes: 'All $t(common.outcomes)',
          all_epics: 'All $t(common.epics)',
          no_epics_found: 'No $t(common.epic, lowercase)s found',
          all_teams: 'All Teams',
          teams: '$t(common.teams)',
          types: 'Types',
          all_types: 'All Types',
          no_teams_found: 'No teams found',
          search_by_epic: 'Search by $t(common.epic)',
          search_by_team: 'Search by Team',
          completed_only: 'Completed Only',
          include_empty: 'Include Empty',
          bookmarked_only: 'Bookmarked Only',
          show_hidden: 'Show Hidden',
          committed_next_weeks: 'Committed',
        },
        initiative_performance: {
          add_target: 'Add a target to start tracking your $t(common.initiative, lowercase) performance',
          throughput: 'Throughput',
          completion: 'Completion',
          focus: 'Focus',
          flow_by_phase: 'Flow By Phase',
          targets: 'Targets',
          milestones: 'Milestones',
          create_target: 'Create Target',
          cost: 'Cost',
          epic_completion: '$t(common.epic) Completion',
          mode: 'Mode',
          cost_by_units: 'Cost By {{units}}',
          search_placeholder: 'Search by $t(common.work_unit) name or ID',
          export: 'Export',
          pick_date: 'Pick date',
          initiative_completion: '$t(common.initiative) Completion',
          initiative_epics: '$t(common.initiative) $t(common.epics)',
          time_elapsed: 'Time Elapsed',
          cost_to_date: 'Cost to Date',
          start_date: 'Start Date',
          end_date: 'End Date',
          activity_start_date: 'Activity Start Date',
          activity_end_date: 'Activity End Date',
          details_title: 'Details',
          selected_date: 'Selected Date',
          completed_tasks: 'Completed $t(common.work_units)',
          team_details: 'Team Details',
          performance_score_current: 'Performance Score (Current)',
          project_manager: 'PM',
          cost_to_date_initiative: 'Cost to Date (This $t(common.initiative))',
          start_date_first_task: 'Start Date (First $t(common.work_unit))',
          task_count_initiative: '$t(common.work_unit) Count (This $t(common.initiative))',
          task_log: '$t(common.work_unit) Log',
          status_changed_to: 'Status changed to',
          changed_by: 'by {{user}}',
          not_found: '$t(common.initiative) not found',
          no_tasks_data_for_month:
            'No completed $t(common.work_units) for this month. Try switching to a different month.',
          labels: {
            initiative_progress: '$t(common.initiative) Progress',
            progress: 'Progress',
            contributing_tasks: 'Contributing $t(common.work_units)',
            contributing_epics: 'Contributing $t(common.epics)',
            contributing_teams: 'Contributing Teams',
            contributing_boards: 'Contributing $t(common.boards)',
            contributing_initiatives: 'Contributing $t(common.initiatives)',
            contributing_outcomes: 'Contributing $t(common.outcomes)',
          },
          related_tasks: {
            title: 'Related $t(common.work_units)',
            table: {
              id: 'ID',
              name: 'Name',
              epic: '$t(common.epic)',
              points: '$t(common.estimation)',
              type: 'Type',
              created: 'Created',
            },
          },
          chart_labels: {
            focus: 'Focus',
            total_tasks: 'Total $t(common.work_units)',
            total_points: 'Total $t(common.estimation)',
            completed_tasks: 'Completed $t(common.work_units)',
            completed_points: 'Completed $t(common.estimation)',
            future_total_tasks: 'Total $t(common.work_units) (Future Projection)',
            future_total_points: 'Total $t(common.estimation) (Future Projection)',
            future_completed_tasks: 'Completed $t(common.work_units) (Future Projection)',
            future_completed_points: 'Completed $t(common.estimation) (Future Projection)',
            per_month: 'per month',
            target_end_date: 'Target End Date',
          },
        },
        epics: {
          due_date: 'Due Date',
          has_due_date: 'Has Due Date',
          no_due_date: 'No Due Date',
          not_started: 'Not Started',
          in_progress: 'In Progress',
          completed: 'Completed',
          completion: '$t(common.epic) Completion',
          created: 'Created',
          scope_change: 'Scope Change (3m)',
          contributing_teams: 'Contributing $t(common.team)(s)',
          parent_project: 'Parent $t(common.team)',
          target_end_date: 'Target End Date',
          created_date: 'Created Date',
          next_90_days: 'Next 90 Days',
          last_90_days: 'Last 90 Days',
        },
      },
      process: {
        section_graph: {
          title: '$t(common.work_units) Process Map',
          legend: {
            expected_steps: 'Expected Steps',
            skipped_steps: 'Skipped Steps',
            backwards_steps: 'Backwards Steps',
            blocked_steps: 'Blocked (Unexpected)',
          },
        },
        scope: {
          task_type: '$t(common.work_units) Type',
          task_type_placeholder: 'Select a $t(common.work_units) type',
        },
        map: {
          considered_work_units: 'Considered $t(common.work_units)',
          scope_selected: 'for the scope selected',
          explanation: {
            what_title: 'What does this map do?',
            what_text:
              'The map details the number of work units (stories, work units, bugs, subtasks) that were completed for the selected time period and filters.',
            how_title: 'How is the map drawn?',
            how_text: 'Each major box matches the project status that was mapped to Bloomfilter during onboarding.',
            process_flow:
              'Lines connect each step vertically to complete the expected path. The thickness of each line visualizes the volume of completed work units that travelled that part of the process flow for the time period selected.',
            status_note:
              'Work unit transitions can cause discrepancy from their source system when one or more customer statuses are used on a work unit in a desired time period.',
            future_note:
              'In future iterations, Bloomfilter will make the Process Map available to the $t(common.board, lowercase)/filter level, but at this time it is only available to analyze the $t(common.portfolio) and Team ($t(common.project, lowercase)) levels.',
          },
        },
        event_graph: {
          event: 'Event',
          event_plural: 'Events',
        },
      },
      financials: {
        details: '$t(common.work_units) Details',
        work_units_in_usd: '$t(common.work_units) in USD',
        estimation_in_usd: '$t(common.estimation) in USD',
        cost_per_task: 'Cost per $t(common.work_units)',
        cost: 'Cost',
        daily_spend: 'Daily Spend',
        tasks_committed: '$t(common.work_units) Committed',
        points_committed: '$t(common.estimation) Committed',
        velocity: 'Velocity',
        throughput: 'Throughput',
        tasks_completed: '$t(common.work_units) Completed',
        points_completed: '$t(common.estimation) Completed',
        cost_per_point: 'Cost per $t(common.estimation_singular)',
        related_work_units: 'Related $t(common.work_units)',
        no_work_units: 'There are no related $t(common.work_units) for this selection.',
        no_filtered_data: 'No data available for the selected filters',
        charts: {
          cost_vs_output: 'Cost vs Output',
          allocation_of_costs: 'Allocation of Costs',
          cost_by_initiative: 'Cost by $t(common.initiative)',
        },
        measure_names: {
          cost: 'Cost',
          daily_burn: 'Daily Burn',
          cost_per_point: 'Cost Per $t(common.estimation_singular)',
          cost_per_task: 'Cost Per $t(common.work_unit)',
          committed_points: 'Committed $t(common.estimation)',
          completed_points: 'Velocity',
          committed_tasks: 'Committed $t(common.work_units)',
          completed_tasks: 'Throughput',
        },
        overview: {
          cost_per_task: 'Cost per $t(common.work_unit)',
          cards: {
            total_budget: 'Total Budget',
            average_monthly: 'Average Monthly',
            average_per_sprint: 'Average per $t(common.sprint)',
            minimum_spend: 'Minimum Spend',
            minimum_per_sprint_spend: 'Minimum per $t(common.sprint) Spend',
            median_monthly_spend: 'Median Monthly Spend',
            median_per_sprint_spend: 'Median per $t(common.sprint) Spend',
            highest_spend: 'Highest Spend',
            highest_per_sprint_spend: 'Highest per $t(common.sprint) Spend',
            descriptions: {
              all_teams_combined: 'All teams combined',
              all_sprints_combined: 'All $t(common.sprint, lowercase)s combined',
              per_team_average: 'Per team average',
              per_sprint_average: 'Per $t(common.sprint, lowercase) average',
              minimum_monthly_spending: 'Minimum monthly spending',
              minimum_per_sprint_spending: 'Minimum per $t(common.sprint, lowercase) spending',
              per_team_median: 'Per team median',
              per_sprint_median: 'Per $t(common.sprint, lowercase) median',
              peak_monthly_spending: 'Peak monthly spending',
              peak_per_sprint_spending: 'Peak per $t(common.sprint, lowercase) spending',
            },
          },
        },
        placeholders: {
          all_teams: 'All Teams',
          all_boards: 'All $t(common.boards)',
          select_team: 'Select a team',
          select_board: 'Select a $t(common.board, lowercase)',
          pick_date_range: 'Pick date range',
          select_sprints: 'Select $t(common.sprint, lowercase)s',
          enter_budget: 'Enter budget',
        },
        table: {
          edit_financial_data: 'Edit Financial Data',
          project_costs: '$t(common.project) Costs',
          project_board: '$t(common.project) / $t(common.board)',
          boards: '$t(common.boards)',
          years: 'Years',
          by_date: 'By Date',
          by_sprint: 'By $t(common.sprint)',
          no_work_periods_found: 'No work periods found',
          no_work_periods_for_year: 'No work periods found for {{year}}',
        },
        messages: {
          all_teams_missing_budget:
            'The All Teams view includes months/teams that do not have a budget amount entered. Edit your financial data to see the most complete picture.',
          team_missing_budget:
            'This view includes months that do not have a budget amount entered. Edit your financial data to see the most complete picture.',
        },
        tooltips: {
          capacity: 'Total number of completed $t(common.work_units) over the time period',
          estimation: 'Total $t(common.estimation) completed over the time period',
          distribution: 'Distribution of work across teams',
        },
      },
      initiatives: {
        section_status: {
          no_end_date: 'The end date is generated because no end date was provided',
        },
        initiative_performance: {
          section_status: {
            cumulative_for_initiative: 'cumulative for $t(common.initiative)',
            total_tasks: 'Total $t(common.work_units)',
            completed_tasks: 'Completed $t(common.work_units)',
          },
        },
      },
      data_management: {
        data_management: 'Data Management',
        unknown_project: 'Unknown $t(common.project)',
        actions: {
          refresh: 'Refresh',
          re_authenticate: 'Re-Authenticate',
          contact_integrator: 'Contact Integrator',
          refresh_in_queue: 'Refresh in queue',
          refresh_in_progress: 'Refresh in progress',
          refresh_success:
            'Data refresh successfully initiated. Check back at this page for updates, or contact Customer Success.',
          refresh_error: 'Failed to initiate data refresh. Please try again or contact Customer Success.',
        },
        workflows: {
          workflows: 'Workflows',
          workflow: 'Workflow',
          assigned_boards: 'Assigned {{boards}}',
          project_board_name: '{{project}} / {{board}} Name',
          active_inactive_statuses: '{{active}} / {{inactive}} {{statuses}}',
          no_boards_selected: 'No {{boards}} selected',
          add_new_workflow: 'Add New Workflow',
          workflow_map: 'Workflow Map',
          mapped: 'Mapped',
          unmapped: 'Unmapped',
          search_and_add_boards: '{{search}} and {{add}} {{boards}}',
          active_statuses: '{{active}} {{statuses}}',
          add_boards_to_view_statuses: 'Add {{boards}} to view {{statuses}} here',
        },
        data_sources: {
          data_sources: 'Data Sources',
          integrated_only: 'Integrated Only',
        },
        integration_health_checker: {
          integration_health_checker: 'Integration Health',
        },
        groups: {
          groups: 'Groups',
        },
        board_settings: {
          board_settings: '{{board}} {{settings}}',
          created_success: '$t(common.board) Settings created successfully',
          created_with_boards_success: '$t(common.board) Settings created with $t(common.board, lowercase)s assigned',
          created_with_mapping_error:
            '$t(common.board) Settings created. Failed to assign some $t(common.board, lowercase)s.',
          create_error: 'Failed to create $t(common.board) Settings',
          name_placeholder: 'Enter $t(common.board) Settings Name',
        },
      },
      integrations: {
        add_new_integrations: 'Add New Integrations',
        add_new_integration: 'Add New Integration',
        success: {
          title: 'Your integration has been started! You will receive an email once the integration is complete.',
          close: 'You can close this tab now',
          integration_started: 'Integration Started',
          integration_started_message: 'Your integration has been started.',
          email_notification: 'You will receive an email once the integration is complete.',
          complete_button: 'Complete',
        },
        settings: {
          yes_assign_estimation: 'Yes, we assign $t(common.estimation) to bugs.',
          no_leave_unpointed: 'No, we leave bugs un-pointed.',
          point_bugs_yes: 'Yes, we assign $t(common.estimation) to bugs.',
          point_bugs_no: 'Nope. We leave them un-pointed.',
        },
      },
      work_periods: {
        burn_down: 'Work Completion',
        burn_up: 'Goal Attainment',
        chart_type_label: 'Chart type',
        date_picker_placeholder: 'Select date',
        no_dates_available: 'No dates available',
        tasks: {
          empty: 'No $t(common.work_units) found',
        },
        task_detail: {
          key: '$t(common.work_unit) $t(common.key)',
          name: '$t(common.name)',
          epic_key: '$t(common.epic) $t(common.key)',
        },
      },
      targets: {
        initiative_name: '$t(common.initiative) $t(common.name)',
        epic_name: '$t(common.parent_task) $t(common.name)',
        task_name: '$t(common.work_unit) $t(common.name)',
        outcome_name: '$t(common.outcome) $t(common.name)',
        thresholds: 'Thresholds',
        threshold: 'Threshold',
        total_subtasks: 'Total $t(common.work_units)',
        risk_category: 'Risk Category',
        risk_score: 'Risk Score',
        green: 'Green',
        yellow: 'Yellow',
        red: 'Red',
      },
      flora: {
        process_analysis: {
          flora_button_tooltip: 'Click to get AI analysis of this data',
          measure_comparison_chart_prompt:
            'Analyze the following data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Find impactful insights from SDLC data.',
          key_measures_over_time_chart_prompt:
            'Analyze the following measure trends over time and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Compare performance across entities and identify notable patterns.',
          initiative_completion_chart_prompt:
            'Analyze the following $t(common.initiative, lowercase) completion data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Identify progress trends and any $t(common.initiative, lowercase)s that may need attention.',
          initiative_focus_chart_prompt:
            'Analyze the following $t(common.initiative, lowercase) focus data. $t(common.initiative) focus means the percentage of total work time spent by each entity (such as a $t(common.team) or $t(common.board)) on this $t(common.initiative, lowercase), out of all work done during the period. Provide a concise summary of the key findings, including impactful bullet points that highlight patterns of focus allocation across $t(common.initiative, lowercase)s.',
          capacity_table_prompt:
            'Analyze the following capacity data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Identify workload distribution patterns across teams.',
          initiative_overview_table_prompt:
            'Analyze the following $t(common.initiative, lowercase) overview data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Identify $t(common.initiative, lowercase)s by status and highlight any that may need attention.',
          monthly_averages_table_prompt:
            'Analyze the following monthly averages data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Compare performance across entities and identify notable trends.',
        },
        work_periods: {
          flora_button_tooltip: 'Click to get AI analysis of this chart/table',
          sprint_comparison_table_prompt:
            'Analyze the following data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Find impactful insights from the SDLC data. Keep in mind these are $t(common.sprint, lowercase) related metrics.',
          sprint_measure_comparison_chart_prompt:
            'Analyze the following data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Find impactful insights from the SDLC data. Keep in mind these are $t(common.sprint, lowercase) related metrics.',
          sprint_measures_over_time_chart_prompt:
            'Analyze the following data and provide a concise summary of the key findings. Keep your response in a short paragraph of a few sentences and impactful bullet points. Find impactful insights from the SDLC data. Keep in mind these are $t(common.sprint, lowercase) related metrics.',
          assessment_view_prompt:
            'Analyze the following $t(common.sprint, lowercase) assessment data and provide insights for the SDLC $t(common.sprint, lowercase). The data includes health score, key measures (strategy, independence, quality, review time, scope creep, readiness, complexity), and goals. Keep your response in a short paragraph with impactful bullet points.',
          work_phase_distribution_chart_prompt:
            'Analyze the following work phase distribution data and provide insights for the SDLC $t(common.sprint, lowercase). The data shows how work units flow through phases (backlog, in progress, review, test, deployable, done) over time. Keep your response in a short paragraph with impactful bullet points.',
          task_flow_prompt:
            'Analyze the following $t(common.work_unit) flow data and provide insights for the SDLC $t(common.sprint, lowercase). The data shows individual $t(common.work_units) organized by their current phase (backlog, blocked, in progress, in review, in test, deployable, done) for a specific date. Identify bottlenecks, blocked work, work distribution patterns, and any $t(common.work_unit, lowercase)s that may need attention. Keep your response in a short paragraph with impactful bullet points. When referencing $t(common.work_units), use its code.',
          work_pace_burn_up_chart_prompt:
            'Analyze the following burn up data and provide insights for the SDLC $t(common.sprint, lowercase). Burn up charts show cumulative work completed over time compared to the ideal pace. Focus on actual vs ideal progress, burn rate (velocity), scope changes, and whether the team is on track to complete all work by the end date. Keep your response in a short paragraph with impactful bullet points.',
          work_pace_burn_down_chart_prompt:
            'Analyze the following burn down data and provide insights for the SDLC $t(common.sprint, lowercase). Burn down charts show remaining work over time compared to the ideal decline. Focus on work remaining vs ideal, burn rate (velocity), scope changes, and whether the team will complete all work by the end date. Keep your response in a short paragraph with impactful bullet points.',
          delivery_confidence_burn_up_chart_prompt:
            'Analyze the following delivery confidence burn up data and provide insights for the SDLC $t(common.sprint, lowercase). Burn up charts show cumulative work completed vs total scope over time. Focus on completion progress, historical performance bands (best/worst case scenarios), scope changes, and delivery confidence based on past team performance. Keep your response in a short paragraph with impactful bullet points.',
          delivery_confidence_burn_down_chart_prompt:
            'Analyze the following delivery confidence burn down data and provide insights for the SDLC $t(common.sprint, lowercase). Burn down charts show remaining work vs scope over time. Focus on work remaining, historical performance bands (best/worst case scenarios), scope changes, and delivery confidence based on past team performance. Keep your response in a short paragraph with impactful bullet points.',
        },
        strategy: {
          flora_button_tooltip: 'Click to get AI analysis of this chart',
          throughput_tab: {
            initiative_progress_chart_prompt:
              'Analyze the following progress data for this $t(common.initiative, lowercase). This includes $t(common.work_unit, lowercase)s or points completed and the total. Future $t(common.work_unit, lowercase)s or points are a projection into the future and not actual results. Keep your response in a short paragraph of a few sentences and impactful bullet points.',
            contributing_epics_chart_prompt:
              'Analyze the following contributing $t(common.epic) data for this $t(common.initiative, lowercase) and provide a concise summary of the key findings. This data includes $t(common.work_unit, lowercase)s or points completed broken down by $t(common.epic) completion over months. Keep your response in a short paragraph of a few sentences and impactful bullet points.',
            contributing_teams_chart_prompt:
              'Analyze the following contributing Team data for this $t(common.initiative, lowercase) and provide a concise summary of the key findings. This data includes $t(common.work_unit, lowercase)s or points completed broken down by Team completion over months. Keep your response in a short paragraph of a few sentences and impactful bullet points.',
          },
          flow_by_phase_tab: {
            chart_prompt:
              'Analyze the following data and provide a concise summary of the key findings. The data includes phase metadata for all cards being worked on in this $t(common.initiative, lowercase). Phases are based on the status of the card at the end of the month. Keep your response in a short paragraph of a few sentences and impactful bullet points.',
          },
          focus_tab: {
            contributing_teams_chart_prompt:
              'Analyze the following contributing Team data for this $t(common.initiative, lowercase) and provide a concise summary of the key findings. This data includes the percentage focus this Team gave over the month on work contributing to this $t(common.initiative, lowercase). Keep your response in a short paragraph of a few sentences and impactful bullet points.',
            contributing_boards_chart_prompt:
              'Analyze the following contributing $t(common.board) data for this $t(common.initiative, lowercase) and provide a concise summary of the key findings. This data includes the percentage focus this $t(common.board) gave over the month on work contributing to this $t(common.initiative, lowercase). Keep your response in a short paragraph of a few sentences and impactful bullet points.',
          },
        },
        chat: {
          user_message: {
            show_more: 'Show more',
            show_less: 'Show less',
          },
        },
      },
      agent_miner: {
        session: 'Session',
        sessions: 'Sessions',
        turn: 'Turn',
        turns: 'Turns',
        event: 'Event',
        events: 'Events',
        source: 'Source',
        user: 'User',
        tokens: 'Tokens (in / out / cache)',
        api_cost: 'API cost',
        started: 'Started',
        category: 'Category',
        last_activity: 'Last activity',
        related_prs: 'Related PRs',
        pull_requests: {
          tab: 'Pull Requests',
          transcript_tab: 'Transcript',
          open_on_github: 'Open on GitHub',
          agent_authorship: 'Agent authorship',
          lines_authored: 'Lines authored',
          survived_to_merge: 'Agent code that survived to merge',
          survived_subtitle: 'Of the agent lines authored in this session, how many reached the merged PR unchanged.',
          not_line_traced: 'Not line-traced',
          agent: 'Agent',
          human: 'Human',
          fed_by: 'Fed by {{count}} agent sessions across {{files}} files',
          merged_code: 'Merged code',
          merged_code_tooltip: 'Lines from this edit landed in a merged pull request.',
          panel_title: 'Pull request',
          views: {
            contributors: 'Contributors',
            code: 'Code',
            lineage: 'Lineage',
          },
          contributors: {
            lines_tile: 'Lines',
            lines_value: '{{agent}} agent · {{human}} human',
            files_tile: 'Files with agent lines',
            files_value: '{{count}} of {{total}}',
            contributions_tile: 'Contributions',
            contributions_value: '{{sessions}} sessions · {{edits}} edits',
            by_source: 'Agent lines by source',
            source_segment: '{{label}} {{lines}}',
            col_session: 'Session',
            col_user: 'User',
            col_source: 'Source',
            col_files: 'Files',
            col_edits: 'Edits',
            col_lines: 'Lines',
            col_share: 'Share',
            matrix_title: 'Lines matched per session and file',
          },
          view_captions: {
            contributors: 'Who wrote this pull request: agent sessions, their authorship, and what survived to merge.',
            code: 'The merged diff of {{files}} files, each added line traced to the edit that wrote it.',
            code_kept: '{{survived}} of {{authored}} agent lines kept',
            lineage: '{{edits}} agent edits → {{files}} files → this pull request.',
          },
          panel: {
            close: 'Close panel',
            root_directory: '(root)',
            session_lines: '{{count}} lines',
            lineage_hint: 'Hover an edit or a file in the diagram to highlight what it is connected to.',
          },
          files: {
            summary: '{{kept}} kept · {{not_merged}} not merged · {{other_agents}} other agents · {{human}} human',
            legend_kept: 'Agent · kept',
            legend_not_merged: 'Agent · not merged',
            legend_other_agents: 'Other agents',
            legend_human: 'Human',
            no_agent_lines: 'No agent lines in this file',
            not_line_traced: 'Not line-traced',
          },
          code: {
            title: 'Agent-written code',
            subtitle: 'The merged diff for each file, with every added line traced back to the edit that wrote it.',
            kept_pct: '{{pct}}% kept',
            no_agent_lines: 'No agent lines',
            view_diff: 'Merged diff',
            view_edits: 'Agent edits',
            tag_agent: 'Agent',
            tag_agent_ambiguous: 'Agent · ambiguous',
            tag_tooltip: 'Turn {{turn}} · {{session}}',
            tag_tooltip_ambiguous: 'Turn {{turn}} · {{session}} · this text appears more than once in the diff',
            tag_tooltip_file_created: 'Turn {{turn}} · {{session}} · file created by the agent',
            legend_current: 'Agent · this session',
            legend_other: 'Agent · other session',
            legend_ambiguous: 'Agent · ambiguous match',
            legend_human: 'Human',
            patch_unavailable: 'Diff not available from GitHub for this file.',
            no_files: 'No files with agent attribution in this pull request.',
            evidence_missing: 'Line evidence has not been built for this file yet.',
            error: 'Failed to load the file diff.',
            retry: 'Retry',
            expand_hunk: 'Show the remaining lines of this hunk ({{count}})',
            show_more_hunks: 'Show remaining hunks ({{count}})',
            col_old: 'Old line',
            col_new: 'New line',
            col_kind: 'Change',
            col_content: 'Content',
            col_attribution: 'Attribution',
            edits_heading: 'Agent edits: {{count}} · this session: {{unique}} unique, {{landed}} landed',
            edits_not_traced: 'This file is not line-traced, so per-edit survival is unavailable.',
            edit_turn: 'Turn {{turn}}',
            edit_this_session: 'this session',
            edit_stats: '{{landed}} of {{unique}} unique lines landed',
            edit_not_merged_toggle: 'Not in merged diff: {{count}}',
            edit_all_landed: 'Every unique line landed in the merge',
            edits_empty: 'No agent edits touched this file.',
            edits_unmatched_hint: 'Without the GitHub diff, agent lines cannot be matched to the merge.',
          },
          lineage: {
            title: "How the agent's code reached this PR",
            subtitle: 'Each agent edit, the file it landed in, and the pull request that merged it.',
            col_edits: 'Agent edits',
            col_files: 'Files in the merge',
            col_pr: 'Pull request',
            turn: 'Turn {{turn}}',
            this_session: 'this session',
            lines_matched: 'Lines matched: {{count}}',
            show_more: 'Show {{count}} more',
            show_less: 'Show less',
            merged: 'Merged {{ago}}',
            not_merged: 'Not merged yet',
          },
          overview: {
            agent_authorship: 'Agent authorship',
            agent_authorship_sub: 'of merged lines',
            survived: 'Survived to merge',
            survived_sub: 'agent code kept',
            time_to_merge: 'Time to merge',
            merged_ago: 'merged {{ago}}',
            not_merged: 'not merged yet',
            lines_changed: 'Lines changed',
            files: '{{count}} files',
          },
          contributing_sessions: 'Contributing sessions',
          this_session: 'THIS',
          files_in_merge: 'Files in the merge',
          empty: 'No pull requests linked to this session.',
          error: 'Failed to load pull requests.',
        },
        session_list: {
          expand: 'View all in Sessions',
          showing: 'Showing {{shown}} of {{total}} — expand to see all',
          empty: 'No sessions to show.',
          empty_for_model: 'No sessions for this model in the selected window.',
          error: 'Failed to load sessions.',
        },
        model_comparison: {
          agentic_sessions: 'Agentic sessions',
          median_cycle_time: 'Median $t(common.work_unit, lowercase) cycle time',
          median_cycle_time_unit: 'days',
          task_throughput: 'Agent $t(common.work_unit, lowercase) throughput',
          task_throughput_unit: '$t(common.work_unit, lowercase)s',
          task_throughput_tooltip:
            'Count of linked $t(common.work_unit, lowercase)s completed by an agentic session in the selected window.',
          rework_rate: 'Rework rate',
          model: 'Model',
          cycle_days: 'Cycle (d)',
          cycle_days_tooltip:
            '$t(common.work_unit) cycle time, not model latency — median days from a $t(common.work_unit, lowercase) entering In Progress to reaching Done, for linked $t(common.work_unit, lowercase)s completed in the window.',
          rework_rate_tooltip:
            'Share of linked $t(common.work_unit, lowercase)s that moved backward in status (e.g. Done back to In Progress) during the window. Lower is better.',
          tasks_per_dev_week: '$t(common.work_units)/dev/wk',
          tasks_per_dev_week_tooltip:
            'Completed linked $t(common.work_unit, lowercase)s per active developer per week in the selected window.',
          cost_per_task: 'API cost/$t(common.work_unit, lowercase)',
          cost_per_task_tooltip:
            "API cost only — total model API spend for this model's sessions divided by completed linked $t(common.work_unit, lowercase)s. Includes spend from sessions without a linked $t(common.work_unit, lowercase); excludes seat/subscription costs.",
          trend: 'Trend',
          trend_tooltip: 'Completed linked $t(common.work_unit, lowercase)s per period across the selected window.',
          cycle_chart_title: 'Median $t(common.work_unit, lowercase) cycle time by model',
          cycle_chart_subtitle:
            'Days from a $t(common.work_unit, lowercase) entering In Progress to Done — $t(common.work_unit, lowercase) cycle time, not model latency',
          cycle_axis_label: 'Days',
          toggle_line: 'Line',
          toggle_bar: 'Bar',
          overview_title: 'Overview',
          view_chart: 'Cycle time',
          view_table: 'All metrics',
          view_usage: 'Token usage',
          provider: 'Model Provider',
          families_label: 'Models',
          show_unknown: 'Show Unknown models',
          allocation_type: 'Time allocation',
          loading: 'Loading model comparison…',
          empty: 'No agentic sessions in this window.',
          empty_filtered: 'No agentic sessions match the selected provider filter.',
          error: 'Failed to load model comparison data.',
        },
        delivery_impact: {
          view_tiles: 'Comparison',
          view_trend: 'Cycle time trend',
          pick_dates: 'Pick dates',
          toggle_line: 'Line',
          toggle_bar: 'Bar',
          ai_label: 'AI-assisted',
          baseline_label: 'Non-AI-linked tasks',
          vs_baseline: 'vs {{baseline}}',
          view_ai_sessions: 'AI sessions',
          ai_sessions_title: 'AI-assisted sessions',
          ai_sessions_empty: 'No $t(common.work_unit, lowercase)-linked agentic sessions in this window.',
          tiles_title: 'Impact at a glance',
          tiles_subtitle:
            'AI-assisted vs baseline delivery for $t(common.work_unit, lowercase)s completed in the selected window.',
          trend_title: 'Median $t(common.work_unit, lowercase) cycle time — AI-assisted vs baseline',
          trend_subtitle:
            'Days from a $t(common.work_unit, lowercase) entering In Progress to Done, per period, split by whether the $t(common.work_unit, lowercase) had a confidently linked agentic session',
          trend_axis_label: 'Days',
          metrics: {
            median_cycle_time: 'Median cycle time',
            median_lead_time: 'Median lead time',
            throughput: '$t(common.work_units) per dev / week',
            rework_rate: 'Rework rate',
            review_to_done: 'Review to done',
            time_to_merge: 'Median time to merge',
          },
          metric_info: {
            median_cycle_time:
              'Median days from a $t(common.work_unit, lowercase) entering In Progress to reaching Done, for $t(common.work_unit, lowercase)s completed in the window. Lower is better.',
            median_lead_time:
              'Median days from a $t(common.work_unit, lowercase) being created to reaching Done, for $t(common.work_unit, lowercase)s completed in the window. Lower is better.',
            throughput:
              'Completed $t(common.work_unit, lowercase)s per assigned developer per week in the selected window. Higher is better; the same developer can appear in both cohorts.',
            rework_rate:
              'Share of $t(common.work_unit, lowercase)s that moved backward in status (e.g. Done back to In Progress) during the window. Lower is better.',
            review_to_done:
              "Median days from a $t(common.work_unit, lowercase)'s last entry into a review-mapped workflow status (per the board's section mapping) to reaching Done, for $t(common.work_unit, lowercase)s that passed through review. Lower is better.",
            time_to_merge:
              'Median days from a $t(common.pull_request, lowercase) opening to being merged, across the merged $t(common.pull_requests, lowercase) linked to each cohort’s completed $t(common.work_unit, lowercase)s. Lower is better.',
          },
          loading: 'Loading delivery impact…',
          empty: 'No completed $t(common.work_unit, lowercase)s in this window.',
          error: 'Failed to load delivery impact data.',
        },
        adoption: {
          overview_title: 'Adoption overview',
          view_trends: 'Trends',
          view_teams: 'Teams',
          view_attribution: 'Attribution',
          team: 'Team',
          pick_dates: 'Pick date range',
          measure_label: 'Measure',
          measure_active_users: 'Active users',
          measure_sessions: 'Sessions',
          measure_adoption_rate: 'Adoption rate',
          active_users: 'Active users',
          active_users_unit: 'of {{eligible}}',
          active_users_tooltip:
            'Users with at least one agent session turn in the window. The denominator is everyone eligible: active agent users plus in-window $t(common.work_unit, lowercase) assignees.',
          adoption_rate: 'Adoption rate',
          adoption_rate_tooltip:
            'Active agent users divided by eligible users (active agent users plus in-window $t(common.work_unit, lowercase) assignees).',
          sessions_kpi: 'Sessions',
          median_turns: 'Median turns / session',
          median_turns_tooltip:
            'Median number of prompt-response turns per session in the window — a depth signal that separates real use from one-and-done tries.',
          trend_title: '{{measure}} over time',
          trend_subtitle:
            'Org-wide adoption per period — the bucket size follows the time-allocation filter. Future periods render as gaps, not zeros.',
          teams_title: 'Team scorecard',
          teams_subtitle:
            'Sessions that touch several teams count for each of them, so team rows are not summable — org totals live in the overview above.',
          col_team: 'Team',
          col_adoption_rate: 'Adoption rate',
          col_users: 'Users',
          col_users_tooltip: 'Active agent users / eligible users on this team in the window.',
          col_avg_turns: 'Avg turns',
          col_avg_turns_tooltip: 'Average prompt-response turns per session attributed to this team.',
          col_sessions_per_user_week: 'Sessions / user·wk',
          col_sessions_per_user_week_tooltip:
            'Sessions per active user per week, derived from the true window width regardless of the bucket size.',
          col_attribution: 'Attribution mix',
          col_attribution_tooltip:
            'How this team’s sessions were resolved to it, starting from the strongest signal: $t(common.work_unit, lowercase) link, repository branch, working directory, then team membership. The figure is the work-linked share.',
          col_trend: 'Trend',
          col_trend_tooltip: 'Sessions per period across the window.',
          attribution_title: 'How sessions map to teams',
          attribution_subtitle:
            '{{total}} sessions resolved at query time, starting from the strongest signal — each session counts once, so the tiers below sum to the total. Nothing is persisted or re-labeled.',
          all_sessions_row: 'All sessions',
          tiers: {
            task_linked: 'Task-linked',
            branch_inferred: 'Repo · branch',
            directory_inferred: 'Repo · directory',
            member_inferred: 'Member-inferred',
            unattributed: 'Unattributed',
          },
          tier_info: {
            task_linked:
              'The session is linked to an SDLC $t(common.work_unit, lowercase) (via branch name, commit, PR, or prompt) and lands on the team that owns it. The strongest signal.',
            branch_inferred:
              'No $t(common.work_unit, lowercase) link, but the session’s git branch exists in exactly one known repository — the repository’s team claims it.',
            directory_inferred:
              'The session’s working directory name uniquely matches a repository. Ambiguous matches are skipped, never guessed.',
            member_inferred:
              'No work signal at all — falls back to the user’s team membership (their single team, or their dominant team by recent $t(common.work_unit, lowercase) activity).',
            unattributed:
              'No reliable signal. Held out of every team row and reported at org level so team numbers stay honest.',
          },
          unattributed_summary:
            '{{sessions}} sessions from {{users}} users could not be mapped to a team ({{share}} of all sessions).',
          unattributed_hint:
            'Branch names that reference $t(common.work_unit, lowercase)s and working in recognized repositories both improve attribution over time.',
          loading: 'Loading adoption…',
          empty: 'No agent activity in this window.',
          empty_filtered: 'No agent activity for the selected teams in this window.',
          error: 'Failed to load adoption data.',
        },
        usage: {
          chart_title: 'Token usage by model',
          chart_subtitle:
            'Tokens consumed per period by each model across all agentic sessions, regardless of whether linked to a $t(common.work_unit, lowercase) — usage over time, not spend',
          toggle_line: 'Line',
          toggle_bar: 'Bar',
          toggle_table: 'Table',
          measure_label: 'Measure',
          measure_total: 'Total tokens',
          measure_input: 'Input tokens',
          measure_output: 'Output tokens',
          measure_cache_read: 'Cache read',
          measure_cache_write: 'Cache write',
          model: 'Model',
          total_tokens: 'Total tokens',
          input_tokens: 'Input',
          output_tokens: 'Output',
          cache_read_tokens: 'Cache read',
          cache_creation_tokens: 'Cache write',
          empty: 'No token usage in this window.',
        },
        task_fit: {
          overview_title: 'Overview',
          agentic_sessions: 'Agentic sessions',
          completed_tasks: 'Completed $t(common.work_units)',
          median_cycle_time: 'Median $t(common.work_unit, lowercase) cycle time',
          median_cycle_time_unit: 'days',
          rework_rate: 'Rework rate',
          matrix_title: 'Which model fits which $t(common.work_unit, lowercase)?',
          matrix_caption:
            'Rework rate by $t(common.work_unit, lowercase) type · lower is better · ring marks the confidently best model per column',
          task_type: '$t(common.work_unit) type',
          model: 'Model',
          provider: 'Model Provider',
          families_label: 'Models',
          show_unknown: 'Show Unknown models',
          cycle_days: 'Cycle (d)',
          cost_per_task: 'API cost/$t(common.work_unit, lowercase)',
          dimension_raw_type: 'Raw type',
          dimension_flora_label: 'Flora label',
          suppressed_tooltip: 'n = {{n}} (min {{min}})',
          cell_tooltip: 'n = {{n}} · {{completed}} completed',
          cell_tooltip_with_range: 'n = {{n}} · {{completed}} completed · likely {{range}}',
          legend_low: 'Lower rework',
          legend_medium: 'Medium rework',
          legend_high: 'Higher rework',
          legend_better: 'Better (relative)',
          legend_middle: 'Middle (relative)',
          legend_worse: 'Worse (relative)',
          legend_suppressed: 'Below sample threshold',
          legend_best: 'Confidently best per column',
          metric_label: 'Metric',
          metric_rework: 'Rework',
          metric_cycle_time: 'Cycle time',
          metric_cost: 'API cost',
          tasks_panel_empty: 'No $t(common.work_units, lowercase) behind this cell.',
          tasks_panel_error: 'Failed to load the cell’s $t(common.work_units, lowercase).',
          tasks_panel_showing: 'Showing {{shown}} of {{total}}',
          tasks_panel_reworked: 'Reworked',
          tasks_panel_cycle: '{{days}}d cycle',
          pick_dates: 'Pick dates',
          loading: 'Loading $t(common.work_unit, lowercase) fit…',
          empty: 'No agentic sessions in this window.',
          empty_filtered: 'No models match the selected filters.',
          error: 'Failed to load $t(common.work_unit, lowercase) fit data.',
        },
      },
      agents: {
        dashboardTitle: 'Agents Dashboard',
        totalSessions: 'Total Sessions',
        uniqueDevelopers: 'Unique Developers',
        avgSessionDuration: 'Avg Session Duration',
        adoptionRate: 'Agent Adoption Rate',
        cycleTimeComparison: 'Cycle Time Over Time',
        withAgent: 'With Agent',
        withoutAgent: 'Without Agent',
        days: 'Days',
        activityOverTime: 'Activity Over Time',
        sessions: 'Sessions',
        tokens: 'Tokens',
        totalTokens: 'Total Tokens',
        sourceDistribution: 'Source Distribution',
        modelBreakdown: 'Model Breakdown',
        turns: 'Turns',
        sessionsPerDeveloper: 'Sessions Per Developer',
        toolUsage: 'Tool Usage',
        successfulCalls: 'Successful Calls',
        errors: 'Errors',
        filters: 'Filters',
        legend: 'Legend',
      },
    },
  },
};

// i18next mutates the resources object, so we need to preserve the original structure
// for later restoration when switching organizations
export const baseDictionary = JSON.parse(JSON.stringify(baseDictionarySource));

// Resource shape for i18next's CustomTypeOptions augmentation (see src/@types/resources.d.ts).
// Source literal (not the JSON.parse'd clone) preserves literal key types for t() inference.
export type BaseDictionary = typeof baseDictionarySource;

i18n.use(initReactI18next).init({
  resources: baseDictionarySource, // Use the source for init (will be mutated)
  lng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    bindI18n: 'loaded languageChanged',
    bindI18nStore: 'added',
    useSuspense: true,
  },
});

// Case-transformation formatters for nested $t() calls. Lets a single
// canonical entry like `common.portfolio: 'Portfolio'` serve every casing
// via `$t(common.portfolio, lowercase)` / `$t(common.portfolio, uppercase)`,
// so re-labelling a term only needs one dictionary edit instead of duplicate
// `*_lowercase` keys.
i18n.services.formatter?.add('lowercase', (value: string) => value.toLowerCase());
i18n.services.formatter?.add('uppercase', (value: string) => value.toUpperCase());

export default i18n;
